import { useState, useMemo, useEffect, useCallback } from "react";
import { Info, DateTime, Interval } from "luxon";
import classnames from "classnames";
import { TbArrowLeft, TbArrowRight } from "react-icons/tb";
import { IoIosBookmark } from "react-icons/io";
import { useLanguage } from "../../context/LanguageContext";
import PropTypes from "prop-types";

// Hook to detect screen size category
function useScreenSize() {
  const [screenSize, setScreenSize] = useState("mobile");

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize("mobile"); // < 768px
      } else if (width >= 768 && width < 1024) {
        setScreenSize("tablet"); // 768px - 1023px (md)
      } else if (width >= 1024 && width < 1280) {
        setScreenSize("desktop"); // 1024px - 1279px (lg)
      } else {
        setScreenSize("large-desktop"); // >= 1280px (xl and 2xl+)
      }
    };

    handleResize(); // Set initial size
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
}

const CalendarComponent = ({ activeDay, setActiveDay, userData }) => {
  const today = DateTime.local();
  const { copy } = useLanguage();

  // Memoize the initial month to prevent recalculation
  const initialMonth = useMemo(() => today.startOf("month"), []);
  const [firstDayOfActiveMonth, setFirstDayOfActiveMonth] =
    useState(initialMonth);

  // Memoize the data to prevent unnecessary recalculations
  const calendarData = useMemo(
    () => ({
      notes: userData?.notes || [],
      milestones: userData?.milestones || [],
    }),
    [userData?.notes, userData?.milestones]
  );

  const weekdays = useMemo(() => Info.weekdays("short"), []);

  const screenSize = useScreenSize();

  const maxMilestonesToShow = useMemo(() => {
    switch (screenSize) {
      case "mobile":
        return 2;
      case "tablet":
        return 3;
      case "desktop":
        return 3;
      case "large-desktop":
        return 3;
      default:
        return 2;
    }
  }, [screenSize]);

  // Memoize the note dates with a stable reference
  const noteDates = useMemo(() => {
    const dates = new Set();
    calendarData.notes.forEach((note) => {
      if (note?.note_date) {
        dates.add(DateTime.fromISO(note.note_date).toISODate());
      }
    });
    return dates;
  }, [calendarData.notes]);

  // Memoize the days of month calculation
  const daysOfMonth = useMemo(() => {
    const start = firstDayOfActiveMonth.startOf("week");
    const end = start.plus({ days: 35 });
    return Interval.fromDateTimes(start, end)
      .splitBy({ day: 1 })
      .map((day) => day.start);
  }, [firstDayOfActiveMonth]);

  // Memoize the milestone map for efficient lookups
  const milestoneMap = useMemo(() => {
    const map = new Map();
    calendarData.milestones.forEach((milestone) => {
      if (milestone?.deadline) {
        const date = DateTime.fromISO(milestone.deadline).toISODate();
        if (!map.has(date)) {
          map.set(date, []);
        }
        map.get(date).push(milestone);
      }
    });
    return map;
  }, [calendarData.milestones]);

  // Memoize the navigation functions
  const goToPreviousMonth = useCallback(
    () => setFirstDayOfActiveMonth((prev) => prev.minus({ month: 1 })),
    []
  );

  const goToNextMonth = useCallback(
    () => setFirstDayOfActiveMonth((prev) => prev.plus({ month: 1 })),
    []
  );

  const goToToday = useCallback(() => {
    setFirstDayOfActiveMonth(initialMonth);
    setActiveDay(today);
  }, [initialMonth, today, setActiveDay]);

  // Memoize the day cell render function
  const renderDayCell = useCallback(
    (dayOfMonth) => {
      const isToday = dayOfMonth.toISODate() === today.toISODate();
      const isActive = activeDay?.toISODate() === dayOfMonth.toISODate();
      const isInCurrentMonth = dayOfMonth.month === firstDayOfActiveMonth.month;
      const hasNote = noteDates.has(dayOfMonth.toISODate());
      const dayMilestones = milestoneMap.get(dayOfMonth.toISODate()) || [];

      return (
        <div
          key={dayOfMonth.toISODate()}
          className={classnames(
            "flex flex-col gap-1 relative p-2 items-start justify-start cursor-pointer overflow-hidden rounded-lg transition-all duration-200 card-base-border h-36",
            {
              "bg-accent-light": isActive,
              "bg-gray-200": !isActive && !isInCurrentMonth,
              "": !isActive && isInCurrentMonth,
            }
          )}
          onClick={() => setActiveDay(dayOfMonth)}
          aria-label={`Select day ${dayOfMonth.day}`}
        >
          <div className="flex justify-between items-start w-full">
            <span className={classnames("text-xl", isToday ? "" : "")}>
              {dayOfMonth.day}
            </span>
            {hasNote && (
              <IoIosBookmark
                className="text-accent text-2xl absolute -top-1 right-2"
                aria-label="Note for this day"
              />
            )}
          </div>

          <div className="w-full mt-1 min-h-[calc(100%-2rem)]">
            <div className="flex flex-col gap-1 w-full max-h-[120px] overflow-y-auto">
              {dayMilestones.slice(0, maxMilestonesToShow).map((m) => (
                <div
                  key={`${m.id}-${dayOfMonth.toISODate()}`}
                  className={classnames(
                    "text-xs overflow-hidden whitespace-nowrap w-full rounded-sm py-0.5 px-1 transition-all duration-200",
                    {
                      "bg-white": isActive || isInCurrentMonth,
                      "task-card-incompleted text-justify": m.status === false,
                      "task-card-completed text-justify": m.status === true,
                      "card-base-border": isActive || isInCurrentMonth,
                      "border border-gray-300": !isActive && !isInCurrentMonth,
                    }
                  )}
                >
                  {m.title}
                </div>
              ))}
              {dayMilestones.length > maxMilestonesToShow && (
                <div className="text-[10px]">
                  +{dayMilestones.length - maxMilestonesToShow} more
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      activeDay,
      firstDayOfActiveMonth,
      maxMilestonesToShow,
      milestoneMap,
      noteDates,
      setActiveDay,
      today,
    ]
  );

  return (
    <div className="card-base card-base-background card-base-border flex flex-col h-full">
      <div className="flex items-center justify-between p-4 rounded-t-lg flex-none">
        <h2 className="text-xl font-bold flex flex-col gap-2">
          {firstDayOfActiveMonth.monthLong}, {firstDayOfActiveMonth.year}
        </h2>
        <div className="flex items-center space-x-4 gap-1">
          <button
            onClick={goToPreviousMonth}
            aria-label="Previous month"
            className="bg-white card-base-border rounded-full p-2 hover:shadow-sm cursor-pointer transition-all duration-200"
          >
            <TbArrowLeft />
          </button>
          <button
            onClick={goToToday}
            aria-label="Go to today"
            className="bg-white card-base-border rounded-full px-5 py-1.5 hover:shadow-sm cursor-pointer transition-all duration-200"
          >
            {copy.calendar.today}
          </button>
          <button
            onClick={goToNextMonth}
            aria-label="Next month"
            className="bg-white card-base-border rounded-full p-2 hover:shadow-sm cursor-pointer transition-all duration-200"
          >
            <TbArrowRight />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 p-2 bg-accent-light text-center font-medium flex-none">
        {weekdays.map((weekday, index) => (
          <div key={index} className="text-sm">
            {weekday}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2 p-2 w-full flex-grow">
        {daysOfMonth.map(renderDayCell)}
      </div>
    </div>
  );
};

CalendarComponent.propTypes = {
  activeDay: PropTypes.instanceOf(DateTime).isRequired,
  setActiveDay: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
};

export default CalendarComponent;
